import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  useTheme,
} from '@mui/material';
import { CheckCircle, Logout } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { 
  DESIGNER_TERMS_HTML, 
  DESIGNER_TERMS_VERSION, 
  DESIGNER_TERMS_EFFECTIVE_DATE 
} from './terms/designerTerms';
import { 
  FUNERAL_DIRECTOR_TERMS_HTML, 
  FUNERAL_DIRECTOR_TERMS_VERSION, 
  FUNERAL_DIRECTOR_TERMS_EFFECTIVE_DATE 
} from './terms/funeralDirectorTerms';

const TERMS_CONFIG = {
  designer: {
    html: DESIGNER_TERMS_HTML,
    version: DESIGNER_TERMS_VERSION,
    effectiveDate: DESIGNER_TERMS_EFFECTIVE_DATE,
  },
  funeral_director: {
    html: FUNERAL_DIRECTOR_TERMS_HTML,
    version: FUNERAL_DIRECTOR_TERMS_VERSION,
    effectiveDate: FUNERAL_DIRECTOR_TERMS_EFFECTIVE_DATE,
  },
};

const TermsAndConditionsModal = ({ open, onAccept, user, termsType = 'designer' }) => {
  const theme = useTheme();
  const { logout } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const termsConfig = TERMS_CONFIG[termsType];
  
  if (!termsConfig) {
    console.error(`Unknown terms type: ${termsType}`);
    return null;
  }

  const handleAccept = async () => {
    setIsSubmitting(true);
    try {
      await onAccept(termsConfig.version);
    } catch (error) {
      console.error('Error accepting terms:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      sx={{ zIndex: 1400 }}
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '20px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2,
      }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            Terms and Conditions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Effective Date: {termsConfig.effectiveDate} | Version: {termsConfig.version}
        </Typography>
      </DialogTitle>
      
      <DialogContent 
        sx={{ 
          flex: 1,
          overflow: 'auto',
          px: 4,
          py: 3,
          backgroundColor: '#fafafa',
        }}
      >
        <Box
          sx={{
            maxWidth: '100%',
            '& h1': {
              fontSize: '1.5rem',
              fontWeight: 600,
              marginBottom: '0.5rem',
              color: '#1d1d1f',
              lineHeight: 1.3,
            },
            '& h2': {
              fontSize: '1.125rem',
              fontWeight: 600,
              marginTop: '2rem',
              marginBottom: '0.75rem',
              color: '#1d1d1f',
              lineHeight: 1.4,
            },
            '& h3': {
              fontSize: '1rem',
              fontWeight: 600,
              marginTop: '1.5rem',
              marginBottom: '0.5rem',
              color: '#1d1d1f',
              lineHeight: 1.4,
            },
            '& p': {
              fontSize: '0.9375rem',
              lineHeight: 1.6,
              color: '#1d1d1f',
              marginBottom: '1rem',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            },
            '& ul, & ol': {
              marginTop: '0.5rem',
              marginBottom: '1rem',
              paddingLeft: '1.5rem',
            },
            '& li': {
              fontSize: '0.9375rem',
              lineHeight: 1.6,
              color: '#1d1d1f',
              marginBottom: '0.5rem',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            },
            '& strong': {
              fontWeight: 600,
              color: '#1d1d1f',
            },
          }}
        >
          {termsConfig.html}
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        borderTop: '1px solid',
        borderColor: '#d2d2d7',
        px: { xs: 2, sm: 4 },
        py: { xs: 2, sm: 3 },
        flexDirection: 'column',
        gap: { xs: 1.5, sm: 2.5 },
        backgroundColor: '#fafafa',
      }}>
        <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={logout}
            startIcon={<Logout />}
            sx={{
              borderColor: '#d2d2d7',
              color: '#1d1d1f',
              '&:hover': {
                borderColor: '#86868b',
                backgroundColor: '#f5f5f7',
              },
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: '12px',
              textTransform: 'none',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            }}
          >
            Decline
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={handleAccept}
            disabled={isSubmitting}
            startIcon={<CheckCircle />}
            sx={{
              backgroundColor: theme => theme.palette.primary.main,
              color: '#ffffff',
              '&:hover': {
                backgroundColor: theme => theme.palette.primary.dark,
              },
              '&:disabled': {
                backgroundColor: '#d2d2d7',
                color: '#86868b',
              },
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: '12px',
              textTransform: 'none',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
              boxShadow: 'none',
              '&:hover:not(:disabled)': {
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            {isSubmitting ? 'Accepting...' : 'Accept'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TermsAndConditionsModal;
export { DESIGNER_TERMS_VERSION, FUNERAL_DIRECTOR_TERMS_VERSION };
